import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Paper,
  Tooltip,
  CircularProgress,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { sqlTemplatesAPI, apiTemplatesAPI } from '../services/api';
import SchemaBuilder from '../components/SchemaBuilder';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const METHOD_COLORS = {
  GET: '#4caf50',
  POST: '#ff9800',
  PUT: '#2196f3',
  DELETE: '#f44336',
  PATCH: '#9c27b0',
};

// ── Monaco JSON Editor (small helper) ────────────────────────
const MonacoJsonField = ({ value, onChange, height = 180, readOnly = false }) => {
  const [text, setText] = useState(value ? JSON.stringify(value, null, 2) : '');
  const [error, setError] = useState('');

  useEffect(() => {
    setText(value ? JSON.stringify(value, null, 2) : '');
  }, [value]);

  const handleChange = (t) => {
    setText(t || '');
    if (!t || !t.trim()) { setError(''); onChange(null); return; }
    try { const p = JSON.parse(t); setError(''); onChange(p); }
    catch { setError('Invalid JSON'); }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: error ? 'error.main' : 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Editor
        height={height}
        language="json"
        theme="vs-dark"
        value={text}
        onChange={readOnly ? undefined : handleChange}
        options={{ minimap: { enabled: false }, lineNumbers: 'on', scrollBeyondLastLine: false, fontSize: 13, tabSize: 2, automaticLayout: true, wordWrap: 'on', readOnly }}
      />
      {error && <Typography variant="caption" color="error" sx={{ px: 1, py: 0.5, display: 'block' }}>{error}</Typography>}
    </Box>
  );
};

// ── View detail dialog (enhanced with Monaco read-only) ──────
const DetailDialog = ({ open, onClose, template, type, onCopy }) => {
  if (!template) return null;

  const renderJson = (label, data) => {
    if (!data) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
          <Tooltip title="Copy JSON"><IconButton size="small" onClick={() => onCopy(JSON.stringify(data, null, 2))}><CopyIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
        <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height={Math.min(200, JSON.stringify(data, null, 2).split('\n').length * 20 + 20)}
            language="json"
            theme="vs-dark"
            value={JSON.stringify(data, null, 2)}
            options={{ minimap: { enabled: false }, lineNumbers: 'off', scrollBeyondLastLine: false, fontSize: 12, readOnly: true, domReadOnly: true, automaticLayout: true, wordWrap: 'on' }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {type === 'sql' ? template.template_name : template.api_name}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {type === 'api' && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={template.http_method} size="small" sx={{ bgcolor: METHOD_COLORS[template.http_method] || '#757575', color: '#fff', fontWeight: 600 }} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{template.base_url || ''}{template.endpoint}</Typography>
          </Box>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Description</Typography>
          <Typography variant="body2">{template.description}</Typography>
        </Box>
        {type === 'sql' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" color="text.secondary">SQL Query</Typography>
              <Tooltip title="Copy SQL"><IconButton size="small" onClick={() => onCopy(template.sql_query)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden' }}>
              <Editor
                height={Math.min(300, (template.sql_query || '').split('\n').length * 20 + 20)}
                language="sql"
                theme="vs-dark"
                value={template.sql_query}
                options={{ minimap: { enabled: false }, lineNumbers: 'on', scrollBeyondLastLine: false, fontSize: 12, readOnly: true, domReadOnly: true, automaticLayout: true, wordWrap: 'on' }}
              />
            </Box>
          </Box>
        )}
        {type === 'sql' && template.database_names?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Databases</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {template.database_names.map((db) => <Chip key={db} label={db} size="small" />)}
            </Box>
          </Box>
        )}
        {type === 'api' && (
          <>
            {template.service_name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Service</Typography>
                <Typography variant="body2">{template.service_name} {template.service_cluster ? `(${template.service_cluster})` : ''}</Typography>
              </Box>
            )}
            {template.tags?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Tags</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {template.tags.map((t) => <Chip key={t} label={t} size="small" />)}
                </Box>
              </Box>
            )}
            {renderJson('Request Body Schema', template.request_body_schema)}
            {renderJson('Response Schema', template.response_schema)}
            {renderJson('Query Parameters', template.query_parameters)}
            {renderJson('Path Parameters', template.path_parameters)}
            {renderJson('Headers', template.headers)}
            {renderJson('Example Request', template.example_request)}
            {renderJson('Example Response', template.example_response)}
          </>
        )}
        {type === 'sql' && renderJson('Parameters', template.parameters)}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Category</Typography>
          <Typography variant="body2">{template.category || '—'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Created by</Typography>
            <Typography variant="body2">{template.created_by}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Created</Typography>
            <Typography variant="body2">{new Date(template.created_at).toLocaleDateString()}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Embedding</Typography>
            <Chip label={template.embedding_status} size="small" color={template.embedding_status === 'COMPLETED' ? 'success' : template.embedding_status === 'FAILED' ? 'error' : 'default'} />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ── SQL Template Full-Page Form ──────────────────────────────
const SqlTemplateForm = ({ editTemplate, onSave, onBack }) => {
  const [form, setForm] = useState({
    template_name: '', sql_query: '', description: '', category: '',
    database_names: [], parameters: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editTemplate) {
      setForm({
        template_name: editTemplate.template_name || '',
        sql_query: editTemplate.sql_query || '',
        description: editTemplate.description || '',
        category: editTemplate.category || '',
        database_names: editTemplate.database_names || [],
        parameters: editTemplate.parameters || null,
      });
    }
  }, [editTemplate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onBack();
    } catch {
      // error shown by parent
    } finally {
      setSaving(false);
    }
  };

  const valid = form.template_name && form.sql_query && form.description;

  return (
    <Box sx={{ p: 3 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          {editTemplate ? `Edit: ${editTemplate.template_name}` : 'Create SQL Template'}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!valid || saving}
          size="large"
        >
          {editTemplate ? 'Update' : 'Create'}
        </Button>
      </Box>

      {/* Section 1 — Metadata */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Template Details</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Template Name *" fullWidth value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })} />
          <TextField label="Description *" fullWidth multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <TextField label="Category" fullWidth value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            options={[]}
            value={form.database_names}
            onChange={(_, newVal) => setForm({ ...form, database_names: newVal })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} key={option} />)
            }
            renderInput={(params) => <TextField {...params} label="Database Names" helperText="Type and press Enter" />}
          />
        </Box>
      </Paper>

      {/* Section 2 — SQL Query */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>SQL Query *</Typography>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height={300}
            language="sql"
            theme="vs-dark"
            value={form.sql_query}
            onChange={(v) => setForm({ ...form, sql_query: v || '' })}
            options={{
              minimap: { enabled: false },
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              fontSize: 14,
              tabSize: 2,
              automaticLayout: true,
              wordWrap: 'on',
            }}
          />
        </Box>
      </Paper>

      {/* Section 3 — Parameters */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Parameters</Typography>
        <SchemaBuilder
          value={form.parameters}
          onChange={(v) => setForm({ ...form, parameters: v })}
          height={200}
        />
      </Paper>
    </Box>
  );
};

// ── API Template Full-Page Form ──────────────────────────────
const ApiTemplateForm = ({ editTemplate, onSave, onBack }) => {
  const [form, setForm] = useState({
    api_name: '', endpoint: '', http_method: 'GET', service_name: '', description: '',
    base_url: '', category: '', tags: [],
    request_body_schema: null, response_schema: null,
    query_parameters: null, path_parameters: null, headers: null,
    auth_type: '', auth_details: null,
    example_request: null, example_response: null,
    service_cluster: '', api_version: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editTemplate) {
      setForm({
        api_name: editTemplate.api_name || '',
        endpoint: editTemplate.endpoint || '',
        http_method: editTemplate.http_method || 'GET',
        service_name: editTemplate.service_name || '',
        description: editTemplate.description || '',
        base_url: editTemplate.base_url || '',
        category: editTemplate.category || '',
        tags: editTemplate.tags || [],
        request_body_schema: editTemplate.request_body_schema || null,
        response_schema: editTemplate.response_schema || null,
        query_parameters: editTemplate.query_parameters || null,
        path_parameters: editTemplate.path_parameters || null,
        headers: editTemplate.headers || null,
        auth_type: editTemplate.auth_type || '',
        auth_details: editTemplate.auth_details || null,
        example_request: editTemplate.example_request || null,
        example_response: editTemplate.example_response || null,
        service_cluster: editTemplate.service_cluster || '',
        api_version: editTemplate.api_version || '',
      });
    }
  }, [editTemplate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onBack();
    } catch {
      // error shown by parent
    } finally {
      setSaving(false);
    }
  };

  const valid = form.api_name && form.endpoint && form.http_method && form.description;

  return (
    <Box sx={{ p: 3 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          {editTemplate ? `Edit: ${editTemplate.api_name}` : 'Create API Template'}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!valid || saving}
          size="large"
        >
          {editTemplate ? 'Update' : 'Create'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* ── Left Column — Core Info ─────────────────────── */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>API Details</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="API Name *" fullWidth value={form.api_name} onChange={(e) => setForm({ ...form, api_name: e.target.value })} />
              <TextField label="Description *" fullWidth multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ minWidth: 130 }}>
                  <InputLabel>Method *</InputLabel>
                  <Select value={form.http_method} label="Method *" onChange={(e) => setForm({ ...form, http_method: e.target.value })}>
                    {HTTP_METHODS.map((m) => (
                      <MenuItem key={m} value={m}>
                        <Chip label={m} size="small" sx={{ bgcolor: METHOD_COLORS[m], color: '#fff', fontWeight: 600, fontSize: 12 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Endpoint *" fullWidth value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} placeholder="/api/v1/resource/{id}" sx={{ '& input': { fontFamily: 'monospace' } }} />
              </Box>

              <TextField label="Base URL" fullWidth value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://api.example.com" />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Service Name" fullWidth value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} />
                <TextField label="Service Cluster" fullWidth value={form.service_cluster} onChange={(e) => setForm({ ...form, service_cluster: e.target.value })} />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Category" fullWidth value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                <TextField label="API Version" fullWidth value={form.api_version} onChange={(e) => setForm({ ...form, api_version: e.target.value })} placeholder="v1" />
              </Box>

              <TextField label="Auth Type" fullWidth value={form.auth_type} onChange={(e) => setForm({ ...form, auth_type: e.target.value })} placeholder="Bearer, Basic, API Key" />

              <Autocomplete
                multiple
                freeSolo
                autoSelect
                options={[]}
                value={form.tags}
                onChange={(_, newVal) => setForm({ ...form, tags: newVal })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} key={option} />)
                }
                renderInput={(params) => <TextField {...params} label="Tags" helperText="Type and press Enter" />}
              />
            </Box>
          </Paper>
        </Grid>

        {/* ── Right Column — Schemas & Examples ───────────── */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, px: 1 }}>Schemas & Data</Typography>

            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Request Body Schema</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SchemaBuilder value={form.request_body_schema} onChange={(v) => setForm({ ...form, request_body_schema: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Response Schema</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SchemaBuilder value={form.response_schema} onChange={(v) => setForm({ ...form, response_schema: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Query Parameters</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SchemaBuilder value={form.query_parameters} onChange={(v) => setForm({ ...form, query_parameters: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Path Parameters</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SchemaBuilder value={form.path_parameters} onChange={(v) => setForm({ ...form, path_parameters: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Headers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SchemaBuilder value={form.headers} onChange={(v) => setForm({ ...form, headers: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Auth Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MonacoJsonField value={form.auth_details} onChange={(v) => setForm({ ...form, auth_details: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Example Request</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MonacoJsonField value={form.example_request} onChange={(v) => setForm({ ...form, example_request: v })} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={600}>Example Response</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MonacoJsonField value={form.example_response} onChange={(v) => setForm({ ...form, example_response: v })} />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// ── Main Page ────────────────────────────────────────────────
const RagTemplates = () => {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // View state: list | createSql | editSql | createApi | editApi
  const [view, setView] = useState('list');
  const [tab, setTab] = useState(0); // 0 = API, 1 = SQL

  // SQL state
  const [sqlTemplates, setSqlTemplates] = useState([]);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [sqlPage, setSqlPage] = useState(0);
  const [sqlRowsPerPage, setSqlRowsPerPage] = useState(25);
  const [sqlSearch, setSqlSearch] = useState('');
  const [sqlSearchResults, setSqlSearchResults] = useState(null);
  const [sqlCategoryFilter, setSqlCategoryFilter] = useState('');
  const [sqlEditTarget, setSqlEditTarget] = useState(null);
  const [sqlViewTarget, setSqlViewTarget] = useState(null);

  // API state
  const [apiTemplates, setApiTemplates] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiPage, setApiPage] = useState(0);
  const [apiRowsPerPage, setApiRowsPerPage] = useState(25);
  const [apiSearch, setApiSearch] = useState('');
  const [apiSearchResults, setApiSearchResults] = useState(null);
  const [apiServiceFilter, setApiServiceFilter] = useState('');
  const [apiMethodFilter, setApiMethodFilter] = useState('');
  const [apiCategoryFilter, setApiCategoryFilter] = useState('');
  const [apiEditTarget, setApiEditTarget] = useState(null);
  const [apiViewTarget, setApiViewTarget] = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  // ── Fetch ────────────────────────────────────────────────
  const fetchSqlTemplates = useCallback(async () => {
    setSqlLoading(true);
    try {
      const params = {};
      if (sqlCategoryFilter) params.category = sqlCategoryFilter;
      const data = await sqlTemplatesAPI.getAll(params);
      setSqlTemplates(data);
      setSqlSearchResults(null);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to load SQL templates');
    } finally {
      setSqlLoading(false);
    }
  }, [sqlCategoryFilter, showError]);

  const fetchApiTemplates = useCallback(async () => {
    setApiLoading(true);
    try {
      const params = {};
      if (apiServiceFilter) params.service_name = apiServiceFilter;
      if (apiMethodFilter) params.http_method = apiMethodFilter;
      if (apiCategoryFilter) params.category = apiCategoryFilter;
      const data = await apiTemplatesAPI.getAll(params);
      setApiTemplates(data);
      setApiSearchResults(null);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to load API templates');
    } finally {
      setApiLoading(false);
    }
  }, [apiServiceFilter, apiMethodFilter, apiCategoryFilter, showError]);

  useEffect(() => { fetchSqlTemplates(); }, [fetchSqlTemplates]);
  useEffect(() => { fetchApiTemplates(); }, [fetchApiTemplates]);

  // ── Search ───────────────────────────────────────────────
  const handleSqlSearch = async () => {
    if (!sqlSearch.trim()) { setSqlSearchResults(null); return; }
    setSqlLoading(true);
    try {
      const results = await sqlTemplatesAPI.search({ query: sqlSearch, top_k: 20, category: sqlCategoryFilter || undefined });
      setSqlSearchResults(results);
    } catch (err) {
      showError(err.response?.data?.detail || 'Search failed');
    } finally {
      setSqlLoading(false);
    }
  };

  const handleApiSearch = async () => {
    if (!apiSearch.trim()) { setApiSearchResults(null); return; }
    setApiLoading(true);
    try {
      const results = await apiTemplatesAPI.search({
        query: apiSearch, top_k: 20,
        service_name: apiServiceFilter || undefined,
        category: apiCategoryFilter || undefined,
        http_method: apiMethodFilter || undefined,
      });
      setApiSearchResults(results);
    } catch (err) {
      showError(err.response?.data?.detail || 'Search failed');
    } finally {
      setApiLoading(false);
    }
  };

  // ── CRUD handlers ────────────────────────────────────────
  const handleSqlSave = async (form) => {
    if (sqlEditTarget) {
      const updatePayload = { ...form, updated_by: user?.full_name || user?.email || 'unknown' };
      delete updatePayload.created_by;
      await sqlTemplatesAPI.update(sqlEditTarget.id, updatePayload);
      showSuccess('SQL template updated');
    } else {
      await sqlTemplatesAPI.create({ ...form, created_by: user?.full_name || user?.email || 'unknown' });
      showSuccess('SQL template created');
    }
    fetchSqlTemplates();
  };

  const handleApiSave = async (form) => {
    if (apiEditTarget) {
      const updatePayload = { ...form, updated_by: user?.full_name || user?.email || 'unknown' };
      delete updatePayload.created_by;
      await apiTemplatesAPI.update(apiEditTarget.id, updatePayload);
      showSuccess('API template updated');
    } else {
      await apiTemplatesAPI.create({ ...form, created_by: user?.full_name || user?.email || 'unknown' });
      showSuccess('API template created');
    }
    fetchApiTemplates();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const deletedBy = user?.full_name || user?.email || 'unknown';
      if (deleteType === 'sql') {
        await sqlTemplatesAPI.delete(deleteTarget.id, deletedBy);
        showSuccess('SQL template deleted');
        fetchSqlTemplates();
      } else {
        await apiTemplatesAPI.delete(deleteTarget.id, deletedBy);
        showSuccess('API template deleted');
        fetchApiTemplates();
      }
    } catch (err) {
      showError(err.response?.data?.detail || 'Delete failed');
    }
    setDeleteTarget(null);
    setDeleteType(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied to clipboard');
  };

  // ── Navigation helpers ───────────────────────────────────
  const goToList = () => {
    setView('list');
    setSqlEditTarget(null);
    setApiEditTarget(null);
  };

  // ── Derived data ─────────────────────────────────────────
  const sqlData = sqlSearchResults || sqlTemplates;
  const apiData = apiSearchResults || apiTemplates;

  const sqlCategories = [...new Set(sqlTemplates.map((t) => t.category).filter(Boolean))];
  const apiCategories = [...new Set(apiTemplates.map((t) => t.category).filter(Boolean))];
  const apiServices = [...new Set(apiTemplates.map((t) => t.service_name).filter(Boolean))];

  // ── Full-page form views ─────────────────────────────────
  if (view === 'createSql' || view === 'editSql') {
    return (
      <SqlTemplateForm
        editTemplate={view === 'editSql' ? sqlEditTarget : null}
        onSave={handleSqlSave}
        onBack={goToList}
      />
    );
  }

  if (view === 'createApi' || view === 'editApi') {
    return (
      <ApiTemplateForm
        editTemplate={view === 'editApi' ? apiEditTarget : null}
        onSave={handleApiSave}
        onBack={goToList}
      />
    );
  }

  // ── List view ────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>API & SQL Library</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`API Templates (${apiTemplates.length})`} />
          <Tab label={`SQL Templates (${sqlTemplates.length})`} />
        </Tabs>
      </Box>

      {/* ── API Templates Tab ──────────────────────────────── */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Semantic search..."
              value={apiSearch}
              onChange={(e) => setApiSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApiSearch()}
              sx={{ minWidth: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Button size="small" variant="outlined" onClick={handleApiSearch}>Search</Button>
            {apiSearchResults && (
              <Button size="small" color="secondary" onClick={() => { setApiSearchResults(null); setApiSearch(''); }}>Clear</Button>
            )}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Method</InputLabel>
              <Select value={apiMethodFilter} label="Method" onChange={(e) => setApiMethodFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {HTTP_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Service</InputLabel>
              <Select value={apiServiceFilter} label="Service" onChange={(e) => setApiServiceFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {apiServices.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Category</InputLabel>
              <Select value={apiCategoryFilter} label="Category" onChange={(e) => setApiCategoryFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {apiCategories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Refresh"><IconButton onClick={fetchApiTemplates}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setApiEditTarget(null); setView('createApi'); }}>
              New API Template
            </Button>
          </Box>

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Created By</TableCell>
                  {apiSearchResults && <TableCell>Similarity</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiLoading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : apiData.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No API templates found</TableCell></TableRow>
                ) : (
                  apiData.slice(apiPage * apiRowsPerPage, apiPage * apiRowsPerPage + apiRowsPerPage).map((tpl) => (
                    <TableRow key={tpl.id} hover sx={{ cursor: 'pointer' }} onClick={() => setApiViewTarget(tpl)}>
                      <TableCell sx={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.api_name}</TableCell>
                      <TableCell><Chip label={tpl.http_method} size="small" sx={{ bgcolor: METHOD_COLORS[tpl.http_method] || '#757575', color: '#fff', fontWeight: 600, fontSize: 11 }} /></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.endpoint}</TableCell>
                      <TableCell>{tpl.service_name || '—'}</TableCell>
                      <TableCell>{tpl.category || '—'}</TableCell>
                      <TableCell>{tpl.created_by}</TableCell>
                      {apiSearchResults && (
                        <TableCell><Chip label={`${(tpl.similarity_score * 100).toFixed(1)}%`} size="small" color={tpl.similarity_score > 0.8 ? 'success' : 'default'} /></TableCell>
                      )}
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View"><IconButton size="small" onClick={() => setApiViewTarget(tpl)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => { setApiEditTarget(tpl); setView('editApi'); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(tpl); setDeleteType('api'); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={apiData.length}
              page={apiPage}
              onPageChange={(_, p) => setApiPage(p)}
              rowsPerPage={apiRowsPerPage}
              onRowsPerPageChange={(e) => { setApiRowsPerPage(parseInt(e.target.value, 10)); setApiPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      )}

      {/* ── SQL Templates Tab ──────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Semantic search..."
              value={sqlSearch}
              onChange={(e) => setSqlSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSqlSearch()}
              sx={{ minWidth: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Button size="small" variant="outlined" onClick={handleSqlSearch}>Search</Button>
            {sqlSearchResults && (
              <Button size="small" color="secondary" onClick={() => { setSqlSearchResults(null); setSqlSearch(''); }}>Clear</Button>
            )}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Category</InputLabel>
              <Select value={sqlCategoryFilter} label="Category" onChange={(e) => setSqlCategoryFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {sqlCategories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Refresh"><IconButton onClick={fetchSqlTemplates}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSqlEditTarget(null); setView('createSql'); }}>
              New SQL Template
            </Button>
          </Box>

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Databases</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Created</TableCell>
                  {sqlSearchResults && <TableCell>Similarity</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sqlLoading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : sqlData.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No SQL templates found</TableCell></TableRow>
                ) : (
                  sqlData.slice(sqlPage * sqlRowsPerPage, sqlPage * sqlRowsPerPage + sqlRowsPerPage).map((tpl) => (
                    <TableRow key={tpl.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSqlViewTarget(tpl)}>
                      <TableCell sx={{ fontWeight: 500, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.template_name}</TableCell>
                      <TableCell>{tpl.category || '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(tpl.database_names || []).map((db) => <Chip key={db} label={db} size="small" variant="outlined" />)}
                          {(!tpl.database_names || tpl.database_names.length === 0) && '—'}
                        </Box>
                      </TableCell>
                      <TableCell>{tpl.created_by}</TableCell>
                      <TableCell>{new Date(tpl.created_at).toLocaleDateString()}</TableCell>
                      {sqlSearchResults && (
                        <TableCell><Chip label={`${(tpl.similarity_score * 100).toFixed(1)}%`} size="small" color={tpl.similarity_score > 0.8 ? 'success' : 'default'} /></TableCell>
                      )}
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Copy SQL"><IconButton size="small" onClick={() => copyToClipboard(tpl.sql_query)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="View"><IconButton size="small" onClick={() => setSqlViewTarget(tpl)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => { setSqlEditTarget(tpl); setView('editSql'); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(tpl); setDeleteType('sql'); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={sqlData.length}
              page={sqlPage}
              onPageChange={(_, p) => setSqlPage(p)}
              rowsPerPage={sqlRowsPerPage}
              onRowsPerPageChange={(e) => { setSqlRowsPerPage(parseInt(e.target.value, 10)); setSqlPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      )}

      {/* ── Detail Dialogs ─────────────────────────────────── */}
      <DetailDialog open={!!sqlViewTarget} onClose={() => setSqlViewTarget(null)} template={sqlViewTarget} type="sql" onCopy={copyToClipboard} />
      <DetailDialog open={!!apiViewTarget} onClose={() => setApiViewTarget(null)} template={apiViewTarget} type="api" onCopy={copyToClipboard} />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteType === 'sql' ? deleteTarget?.template_name : deleteTarget?.api_name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RagTemplates;
